
import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  Fab,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  HelpOutlineRounded as HelpIcon,
  RefreshRounded as RefreshIcon,
  FileDownloadOutlined as ExportIcon,
  AddRounded as CreateIcon,
  ArrowDropDownRounded as SplitArrowIcon,
} from '@mui/icons-material';
import { RowActionsMenu, type RowAction } from './RowActionsMenu';

export interface SectionHeaderAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Tooltip text; defaults to `label` when omitted. */
  tooltip?: string;
}

/** A secondary way to create — an entry of the create split button's menu. */
export interface SectionHeaderCreateMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

/** The create action, optionally with other ways to create behind a split button. */
export interface SectionHeaderCreate extends SectionHeaderAction {
  /**
   * Other ways to create the same thing («Desde plantilla», «Importar»). From `sm` up the create
   * becomes a split button — the main click does `onClick`, the arrow opens these; below `sm` the
   * floating button does `onClick` and these join the overflow menu.
   */
  menu?: SectionHeaderCreateMenuItem[];
}

export interface SectionHeaderProps {
  /** Left zone: context only — pill tabs, a scope row, or inline search. Screen-owned content. */
  left?: React.ReactNode;
  /** Help pill (leftmost of the right cluster). Omit to hide. */
  help?: SectionHeaderAction;
  /** Filters slot — render a `<FilterPopover>` or `<SelfFilterPopover>` trigger here. Omit to hide. */
  filters?: React.ReactNode;
  /** Refresh pill. Omit `onRefresh` to hide the control entirely. */
  onRefresh?: () => void;
  refreshLabel?: string;
  refreshing?: boolean;
  /** Export pill, rendered after the │ divider │. Omit to hide. */
  export?: SectionHeaderAction;
  /**
   * Screen-specific resource actions (outlined pills, no icon), rendered between Export and Create
   * per the application-shell spec's "extra actions" slot. Use sparingly — most screens need none.
   */
  actions?: SectionHeaderAction[];
  /** Create button (contained, the CTA fill), last in the cluster; a floating button below
   *  `sm`. Omit to hide. */
  create?: SectionHeaderCreate;
  /** Set to `false` on screens where this header sits in a fixed, always-visible
   *  area that never scrolls out of view (e.g. a `fillHeight` `CustomDatagrid`
   *  screen) — sticky positioning is then redundant and, if the scrolling
   *  ancestor ever has even a few px of residual overflow, can visually
   *  conflict with the table's own sticky header. Defaults to `true`. */
  sticky?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Sticky title/toolbar row at the top of a screen — borderless, sitting on the
 * arena canvas (not a white card), per the application-shell spec
 * (engineering handbook `docs/design-system/foundations.md` §7.14).
 *
 * Left zone is free-form context (pill tabs / scope row / inline search).
 * The right cluster renders in a FIXED order — Help → Filters → Refresh →
 * │ divider │ → Export → Create — and only the slots a screen actually
 * passes are rendered; the divider itself only appears when at least one of
 * Export/Create is present alongside Help/Filters/Refresh.
 *
 * Below `sm` (hybrid-3c §12) the row keeps the title, the filters trigger, refresh as a 44px icon
 * button and ONE overflow menu holding help, the extra actions, export and the other ways to
 * create; create itself becomes a floating button at the bottom right. From `sm` up both zones
 * wrap instead of overflowing, so a long title never runs under a control.
 *
 * This component does not own filter state — the `filters` slot is meant to
 * hold a `<FilterPopover>` (react-admin `<List>` screens) or
 * `<SelfFilterPopover>` (screens with their own filter state). See
 * `src/components/shell/README.md`.
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  left,
  help,
  filters,
  onRefresh,
  refreshLabel: refreshLabelProp,
  refreshing = false,
  export: exportAction,
  actions,
  create,
  sticky = true,
  sx,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const refreshLabel = refreshLabelProp ?? translate('ra.action.refresh');
  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(null);

  // The divider only separates the "utility" actions (Help/Filters/Refresh)
  // from the "resource" actions (Export/Create) — it appears only when both
  // groups have at least one rendered control, never as a dangling separator.
  const hasUtilityActions = Boolean(help || filters || onRefresh);
  const hasResourceActions = Boolean(exportAction || (actions && actions.length > 0) || create);
  const showDivider = hasUtilityActions && hasResourceActions;
  const createMenu = create?.menu ?? [];

  const containerSx: SxProps<Theme> = [
    {
      ...(sticky && { position: 'sticky', top: 0, zIndex: theme.zIndex.appBar - 1 }),
      // Borderless on the arena canvas (Cris's call): the title/toolbar sits
      // directly on the page background instead of a white card. The bg is the
      // canvas colour (not transparent) so a sticky header still occludes the
      // rows scrolling under it.
      backgroundColor: theme.palette.background.default,
      padding: '12px 4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      flexWrap: 'wrap',
    },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  // The left zone wraps and may shrink, so a long title breaks onto a second
  // line instead of running under the controls. Its basis is a title's width,
  // not its content's: with `auto` a long subtitle claimed its whole line
  // length, and the actions dropped to a row of their own under the title even
  // where the two fit side by side — a full toolbar's height lost above every
  // table. The actions only wrap below when title and actions really do not fit.
  const leftZone = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 18rem' }}>
      {left}
    </Box>
  );

  if (isPhone) {
    const overflow: RowAction[] = [
      ...(help
        ? [
            {
              key: 'help',
              label: help.label,
              icon: <HelpIcon fontSize="small" />,
              onClick: help.onClick,
              disabled: help.disabled,
            },
          ]
        : []),
      ...(actions ?? []).map((action, index) => ({
        key: `action-${index}`,
        label: action.label,
        onClick: action.onClick,
        disabled: action.disabled,
      })),
      ...(exportAction
        ? [
            {
              key: 'export',
              label: exportAction.label,
              icon: <ExportIcon fontSize="small" />,
              onClick: exportAction.onClick,
              disabled: exportAction.disabled,
            },
          ]
        : []),
      ...createMenu.map((item, index) => ({
        key: `create-${index}`,
        label: item.label,
        icon: item.icon,
        onClick: item.onClick,
        disabled: create?.disabled,
      })),
    ];

    return (
      <Box sx={containerSx}>
        {leftZone}
        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
          {filters}
          {onRefresh && (
            <Tooltip title={refreshLabel}>
              <span>
                <IconButton onClick={onRefresh} disabled={refreshing} aria-label={refreshLabel}>
                  {refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <RowActionsMenu actions={overflow} triggerLabel={translate('shell.sectionHeader.more')} />
        </Stack>
        {create && (
          <Fab
            aria-label={create.label}
            onClick={create.onClick}
            disabled={create.disabled}
            sx={{
              position: 'fixed',
              right: 16,
              // Clears the phone's home indicator, and a sticky save bar when the
              // page has one, so the floating button never covers the save action.
              bottom: 'calc(16px + env(safe-area-inset-bottom))',
              'body:has([data-sticky-form-actions]) &': {
                bottom: 'calc(88px + env(safe-area-inset-bottom))',
              },
              zIndex: theme.zIndex.speedDial,
              color: theme.palette.common.white,
              backgroundColor: theme.custom.ctaFill,
              '&:hover': { backgroundColor: theme.custom.ctaFillHover },
            }}
          >
            <CreateIcon />
          </Fab>
        )}
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      {leftZone}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="flex-end"
        useFlexGap
        sx={{ minWidth: 0 }}
      >
        {help && (
          <Tooltip title={help.tooltip ?? help.label}>
            <span>
              <Button
                variant="outlined"
                startIcon={<HelpIcon />}
                onClick={help.onClick}
                disabled={help.disabled}
                aria-label={help.label}
              >
                {help.label}
              </Button>
            </span>
          </Tooltip>
        )}

        {filters}

        {onRefresh && (
          <Tooltip title={refreshLabel}>
            <span>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                onClick={onRefresh}
                disabled={refreshing}
                aria-label={refreshLabel}
              >
                {refreshLabel}
              </Button>
            </span>
          </Tooltip>
        )}

        {showDivider && <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />}

        {exportAction && (
          <Tooltip title={exportAction.tooltip ?? exportAction.label}>
            <span>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={exportAction.onClick}
                disabled={exportAction.disabled}
              >
                {exportAction.label}
              </Button>
            </span>
          </Tooltip>
        )}

        {actions?.map((action) => (
          <Tooltip key={action.label} title={action.tooltip ?? action.label}>
            <span>
              <Button variant="outlined" onClick={action.onClick} disabled={action.disabled}>
                {action.label}
              </Button>
            </span>
          </Tooltip>
        ))}

        {create && createMenu.length === 0 && (
          <Tooltip title={create.tooltip ?? create.label}>
            <span>
              <Button
                variant="contained"
                startIcon={<CreateIcon />}
                onClick={create.onClick}
                disabled={create.disabled}
              >
                {create.label}
              </Button>
            </span>
          </Tooltip>
        )}

        {create && createMenu.length > 0 && (
          <>
            {/* One create, several ways to do it: the main click is the usual one,
                the arrow offers the others. */}
            <ButtonGroup variant="contained" disabled={create.disabled}>
              <Button startIcon={<CreateIcon />} onClick={create.onClick}>
                {create.label}
              </Button>
              <Button
                aria-label={translate('shell.sectionHeader.moreCreate')}
                aria-haspopup="menu"
                aria-expanded={Boolean(createMenuAnchor)}
                onClick={(event) => setCreateMenuAnchor(event.currentTarget)}
                sx={{ px: 0.5, minWidth: 0 }}
              >
                <SplitArrowIcon />
              </Button>
            </ButtonGroup>
            <Menu
              anchorEl={createMenuAnchor}
              open={Boolean(createMenuAnchor)}
              onClose={() => setCreateMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {createMenu.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    setCreateMenuAnchor(null);
                    item.onClick();
                  }}
                >
                  {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default SectionHeader;
